import { createApp, defineComponent, h, ref } from 'vue'
import { ElButton, ElDialog } from 'element-plus'

function pad(value) {
  return String(value).padStart(2, '0')
}

function formatDatetimeLocal(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function formatDateLocal(date) {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

function parseDateTime(value) {
  if (!value) return null
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value
  }
  const normalized = String(value).replace(' ', 'T')
  const date = new Date(normalized)
  return Number.isNaN(date.getTime()) ? null : date
}

function normalizeDateTime(value) {
  return value ? `${value}:00` : ''
}

function buildPostponeTime(minutes) {
  return formatDatetimeLocal(new Date(Date.now() + minutes * 60 * 1000))
}

function buildPostponePayload(minutes) {
  return normalizeDateTime(buildPostponeTime(minutes))
}

function buildTodoDueDate(item) {
  const date = parseDateTime(item?.deadlineTime)
  return date ? formatDateLocal(date) : ''
}

function isFutureDatetimeLocal(value) {
  if (!value) return false
  const selected = new Date(value)
  return !Number.isNaN(selected.getTime()) && selected.getTime() > Date.now()
}

function resolveReminderCreatorText(item) {
  if (item?.creatorName) return item.creatorName
  if (item?.creatorId) return `ID: ${item.creatorId}`
  return '未知发起人'
}

function buildActionButton(label, value, currentValue, onClick) {
  const selected = value === currentValue
  return h('button', {
    type: 'button',
    class: [
      'flex min-h-[42px] flex-1 items-center justify-center rounded-xl border px-3 py-2 text-center text-sm font-medium leading-5 transition-colors',
      selected
        ? 'border-orange-400 bg-orange-50 text-orange-700'
        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900'
    ],
    onClick: () => onClick(value)
  }, label)
}

function buildQuickButton(label, value, currentValue, onClick) {
  const selected = value === currentValue
  return h('button', {
    type: 'button',
    class: [
      'rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
      selected
        ? 'border-orange-400 bg-orange-50 text-orange-700'
        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:text-slate-900'
    ],
    onClick: () => onClick(value)
  }, label)
}

function buildField(label, required, children) {
  return h('label', { class: 'block space-y-1.5' }, [
    h('span', { class: 'text-xs font-medium text-slate-600' }, [
      label,
      required ? h('span', { class: 'ml-0.5 text-red-500' }, '*') : null
    ]),
    children
  ])
}

export function chooseReminderTaskAction(item) {
  return new Promise((resolve, reject) => {
    const mountNode = document.createElement('div')
    document.body.appendChild(mountNode)

    let settled = false
    let app

    const DialogComponent = defineComponent({
      name: 'ReminderTaskProcessDialog',
      setup() {
        const visible = ref(true)
        const action = ref('complete')
        const quickDelay = ref('30')
        const customTime = ref(buildPostponeTime(30))
        const todoForm = ref({
          title: item?.title || item?.taskNo || '未命名提醒',
          description: item?.content || '',
          priority: '2',
          dueDate: buildTodoDueDate(item)
        })
        const errorText = ref('')

        function closeDialog() {
          visible.value = false
        }

        function cleanup() {
          app.unmount()
          mountNode.remove()
        }

        function cancel() {
          if (settled) return
          settled = true
          reject('cancel')
          closeDialog()
        }

        function submit() {
          if (action.value === 'complete') {
            settled = true
            resolve({ action: 'complete' })
            closeDialog()
            return
          }
          if (action.value === 'todo') {
            if (!todoForm.value.title || !todoForm.value.title.trim()) {
              errorText.value = '请输入待办标题'
              return
            }
            const todoPayload = {
              title: todoForm.value.title.trim(),
              description: todoForm.value.description || '',
              priority: Number(todoForm.value.priority || 2)
            }
            if (todoForm.value.dueDate) {
              todoPayload.dueDate = `${todoForm.value.dueDate}T23:59:59`
            }
            settled = true
            resolve({
              action: 'todo',
              todo: todoPayload
            })
            closeDialog()
            return
          }
          if (quickDelay.value !== 'custom') {
            settled = true
            resolve({
              action: 'postpone',
              nextSendTime: buildPostponePayload(Number(quickDelay.value))
            })
            closeDialog()
            return
          }
          if (!isFutureDatetimeLocal(customTime.value)) {
            errorText.value = '请选择晚于当前时间的延期提醒时间'
            return
          }
          settled = true
          resolve({
            action: 'postpone',
            nextSendTime: normalizeDateTime(customTime.value)
          })
          closeDialog()
        }

        return () => h(ElDialog, {
          modelValue: visible.value,
          title: '处理提醒',
          width: '440px',
          closeOnClickModal: false,
          destroyOnClose: true,
          'onUpdate:modelValue': value => {
            visible.value = value
            if (!value && !settled) cancel()
          },
          onClosed: cleanup
        }, {
          default: () => [
            h('div', { class: 'space-y-4' }, [
              h('div', { class: 'rounded-xl border border-slate-200 bg-slate-50 px-4 py-3' }, [
                h('p', { class: 'truncate text-sm font-semibold text-slate-900' }, item?.title || item?.taskNo || '未命名提醒'),
                h('p', { class: 'mt-1 text-xs text-slate-500' }, `发起人：${resolveReminderCreatorText(item)}`),
                item?.content
                  ? h('p', { class: 'mt-1 line-clamp-2 text-xs leading-5 text-slate-500' }, item.content)
                  : null
              ]),
              h('div', { class: 'space-y-2' }, [
                h('p', { class: 'text-xs font-medium text-slate-500' }, '处理方式'),
                h('div', { class: 'flex gap-2' }, [
                  buildActionButton('已完成', 'complete', action.value, value => {
                    action.value = value
                    errorText.value = ''
                  }),
                  buildActionButton('延期提醒', 'postpone', action.value, value => {
                    action.value = value
                    errorText.value = ''
                  }),
                  buildActionButton('转为待办', 'todo', action.value, value => {
                    action.value = value
                    errorText.value = ''
                  })
                ])
              ]),
              action.value === 'postpone'
                ? h('div', { class: 'space-y-3 rounded-xl border border-orange-100 bg-orange-50/60 px-4 py-3' }, [
                    h('p', { class: 'text-xs font-medium text-orange-700' }, '延期时长'),
                    h('div', { class: 'flex flex-wrap gap-2' }, [
                      buildQuickButton('半小时', '30', quickDelay.value, value => {
                        quickDelay.value = value
                        errorText.value = ''
                      }),
                      buildQuickButton('一小时', '60', quickDelay.value, value => {
                        quickDelay.value = value
                        errorText.value = ''
                      }),
                      buildQuickButton('一天', '1440', quickDelay.value, value => {
                        quickDelay.value = value
                        errorText.value = ''
                      }),
                      buildQuickButton('自定义', 'custom', quickDelay.value, value => {
                        quickDelay.value = value
                        errorText.value = ''
                      })
                    ]),
                    quickDelay.value === 'custom'
                      ? h('input', {
                          type: 'datetime-local',
                          value: customTime.value,
                          class: 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition-colors focus:border-orange-400',
                          onInput: event => {
                            customTime.value = event.target.value
                            errorText.value = ''
                          }
                        })
                      : null,
                    errorText.value
                      ? h('p', { class: 'text-xs text-red-500' }, errorText.value)
                      : null
                  ])
                : null,
              action.value === 'todo'
                ? h('div', { class: 'space-y-3 rounded-xl border border-blue-100 bg-blue-50/60 px-4 py-3' }, [
                    h('p', { class: 'text-xs font-medium text-blue-700' }, '待办信息'),
                    buildField('标题', true, h('input', {
                      type: 'text',
                      value: todoForm.value.title,
                      maxlength: 200,
                      class: 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition-colors focus:border-blue-400',
                      onInput: event => {
                        todoForm.value.title = event.target.value
                        errorText.value = ''
                      }
                    })),
                    buildField('描述', false, h('textarea', {
                      value: todoForm.value.description,
                      rows: 3,
                      class: 'w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm leading-5 text-slate-700 outline-none transition-colors focus:border-blue-400',
                      onInput: event => {
                        todoForm.value.description = event.target.value
                      }
                    })),
                    h('div', { class: 'grid grid-cols-1 gap-3 sm:grid-cols-2' }, [
                      buildField('优先级', false, h('select', {
                        value: todoForm.value.priority,
                        class: 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition-colors focus:border-blue-400',
                        onChange: event => {
                          todoForm.value.priority = event.target.value
                        }
                      }, [
                        h('option', { value: '1' }, '低'),
                        h('option', { value: '2' }, '中'),
                        h('option', { value: '3' }, '高'),
                        h('option', { value: '4' }, '紧急')
                      ])),
                      buildField('到期时间', false, h('input', {
                        type: 'date',
                        value: todoForm.value.dueDate,
                        class: 'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition-colors focus:border-blue-400',
                        onInput: event => {
                          todoForm.value.dueDate = event.target.value
                        }
                      }))
                    ]),
                    errorText.value
                      ? h('p', { class: 'text-xs text-red-500' }, errorText.value)
                      : null
                  ])
                : null
            ])
          ],
          footer: () => h('div', { class: 'flex justify-end gap-2' }, [
            h(ElButton, { onClick: cancel }, () => '取消'),
            h(ElButton, { type: 'primary', onClick: submit }, () => {
              if (action.value === 'complete') return '确认完成'
              if (action.value === 'todo') return '转为待办'
              return '确认延期'
            })
          ])
        })
      }
    })

    app = createApp(DialogComponent)
    app.mount(mountNode)
  })
}

export function reminderTaskActionMessage(action) {
  if (action === 'complete') return '提醒已完成'
  if (action === 'postpone') return '已延期提醒'
  if (action === 'todo') return '已转为待办'
  return '操作成功'
}
